/**
 * Scanner hooks — typed TanStack Query wrappers for the alpha scanner.
 *
 * Replaces: js/modules/scanner.js (633 lines of vanilla DOM manipulation)
 * Backend:  backend/alpha/interfaces.py + backend/market/interfaces.py
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { apiGet, apiPost } from "@/lib/api-client";
import type { ContrarianOpportunity } from "@/sdk/types/market";

export const SCANNER_QUERY_KEYS = {
  results: ["scanner", "results"] as const,
  jobStatus: (jobId: string) => ["scanner", "job", jobId] as const,
};

/** POST /api/scanner/quick response (backend/routers/scanner.py scan_markets_for_alpha). */
export interface QuickScanResponse {
  status: string;
  scan_params: {
    markets_analyzed: number;
    min_edge: number;
    bankroll: number;
  };
  summary: {
    opportunities_found: number;
    total_edge_percent: number;
    avg_confidence: number;
    strong_signals: number;
  };
  opportunities: unknown[];
  all_results?: unknown[];
}

export function useQuickScan() {
  return useMutation({
    mutationFn: (params?: { limit?: number; bankroll?: number }) =>
      apiPost<QuickScanResponse>("/api/scanner/quick", params),
  });
}

export function useFullScan() {
  return useMutation({
    mutationFn: (params?: {
      bankroll?: number;
      limit?: number;
      market_category?: string;
      include_cross_market?: boolean;
      include_correlation_filter?: boolean;
      include_resolution_proximity?: boolean;
      include_category_rotation?: boolean;
    }) => {
      // params.bankroll is deliberately NOT forwarded: ScanRequest on
      // POST /api/scanner/scan (backend/routers/scanner.py) has no bankroll
      // field and the route hard-codes bankroll=10000 server-side. The param
      // stays in the signature for caller compatibility; sending it would
      // only fake configurability. Pinned by use-scanner.test.tsx.
      const payload: Record<string, boolean | number | string> = {
        discover_enabled_markets: true,
        market_fetch_limit: typeof params?.limit === "number" ? params.limit : 50,
        active_only: true,
        include_cross_market: params?.include_cross_market ?? true,
        include_correlation_filter: params?.include_correlation_filter ?? true,
        include_resolution_proximity: params?.include_resolution_proximity ?? true,
        include_category_rotation: params?.include_category_rotation ?? true,
      };

      if (typeof params?.market_category === "string" && params.market_category.trim()) {
        payload.market_category = params.market_category.trim();
      }

      return apiPost<Record<string, unknown>>(
        "/api/scanner/scan",
        payload,
      );
    },
  });
}

// Compatibility helpers for older feature components.
export function useRunQuickScan() {
  return useQuickScan();
}

export function useRunAnalysis() {
  return useMutation({
    mutationFn: (params: { market_id?: string; mode?: string; bankroll?: number }) => {
      if (!params.market_id) {
        throw new Error("market_id is required");
      }

      const search = new URLSearchParams();
      if (typeof params.bankroll === "number") {
        search.set("bankroll", String(params.bankroll));
      }

      const query = search.toString();
      const path = `/api/scanner/market/${encodeURIComponent(params.market_id)}${query ? `?${query}` : ""}`;
      return apiGet<Record<string, unknown>>(path);
    },
  });
}

export function useScanOpportunities(limit = 20) {
  return useQuery({
    queryKey: ["scanner", "opportunities", limit],
    queryFn: () =>
      apiGet<{ opportunities: ContrarianOpportunity[] }>(
        `/api/signals/contrarian?limit=${limit}`,
      ).then((response) => ({
        opportunities: response.opportunities ?? [],
        total: response.opportunities?.length ?? 0,
      })),
    refetchInterval: 30_000,
  });
}

export function useScanJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: SCANNER_QUERY_KEYS.jobStatus(jobId ?? ""),
    queryFn: () =>
      apiGet<{ status: string; progress: number; results?: unknown[] }>(
        `/api/scanner/job/${jobId}`,
      ),
    enabled: !!jobId,
    refetchInterval: 2_000,
  });
}
